import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "../utils/axiosConfig";
import "../styles/pages/Login.css";

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const uCode = searchParams.get("uCode") || searchParams.get("code");

  const [formData, setFormData] = useState({ emailOrPhone: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [unverifiedUser, setUnverifiedUser] = useState(null);

  useEffect(() => {
    if (uCode) {
      sessionStorage.setItem("pendingJoinCode", uCode.trim());
    }

    const user = localStorage.getItem("user");
    if (user) {
      if (uCode) {
        api.post("/groups/join", { code: uCode.trim() })
          .then((res) => {
            if (res.data?.groupId) {
              sessionStorage.removeItem("pendingJoinCode");
              let dashboard = "/user-dashboard";
              try { const p = JSON.parse(user); if (p.role === 'admin') dashboard = "/admin-dashboard"; } catch (e) { }
              navigate(dashboard, { replace: true });
              setTimeout(() => navigate(`/group/${res.data.groupId}`), 10);
            } else {
              navigate("/user-dashboard", { replace: true });
            }
          })
          .catch(() => {
            navigate("/user-dashboard", { replace: true });
          });
      }
    }
  }, [uCode, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const res = await api.post("/auth/login", formData);
      setSuccess(res.data.message);
      // Token is now set in HttpOnly cookies securely by backend
      const fullUser = { ...res.data.user, role: res.data.role };
      localStorage.setItem("user", JSON.stringify(fullUser));

      const pendingCode = sessionStorage.getItem("pendingJoinCode") || uCode;
      if (pendingCode) {
        sessionStorage.removeItem("pendingJoinCode");
        try {
          const joinRes = await api.post("/groups/join", { code: pendingCode });
          if (joinRes.data?.groupId) {
            const dashboardPath = fullUser.role === "admin" ? "/admin-dashboard" : "/user-dashboard";
            navigate(dashboardPath, { replace: true });
            setTimeout(() => {
              if (!fullUser.selfie) {
                navigate("/selfie-capture");
              } else {
                navigate(`/group/${joinRes.data.groupId}`);
              }
            }, 10);
            return;
          }
        } catch (joinErr) {
          console.error("Auto-join error:", joinErr);
        }
      }

      if (res.data.role === "admin") {
        navigate("/admin-dashboard");
      } else {
        navigate("/user-dashboard");
      }
    } catch (err) {
      const errMsg = err.response?.data?.message || "There was a problem logging in!";
      setError(errMsg);
      if (
        err.response?.data?.isUnverified ||
        errMsg.toLowerCase().includes("verify your email") ||
        errMsg.toLowerCase().includes("not verified")
      ) {
        setUnverifiedUser(err.response?.data?.identifier || formData.emailOrPhone.trim());
      } else {
        setUnverifiedUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        {/* Left Side Banner (Desktop Only) */}
        <div className="a12">
          <div className="form-img">
            <img src="/images/login-banner.jpg" alt="banner" className="c" />
            <div className="logo-wrapper">
              <a href="https://Fotographiya.com" target="_blank" rel="noopener noreferrer">
                <img src="/images/logo_colored_white.png" alt="Fotographiya" width="1299" height="192" />
              </a>
            </div>
            <div className="overlaytext">
              <p>
                "We launched Fotographiya with a vision in 2023, and we’ve achieved a lot on this journey. My aim is to bootstrap with passion and bring a change in the photography industry.”
              </p>
              <h4>Mohit Barthunia</h4>
              <p style={{ fontSize: "12px" }}>Founder of Fotographiya</p>
            </div>
          </div>
        </div>

        {/* Right Side Form */}
        <div className="b12">
          <div className="login-form">
            <div className="mobile-only-logo">
              <img src="/images/logo_colored_white.png" alt="Fotographiya" width="1299" height="192" />
            </div>

            <div className="loginfrom-top">
              <h2>🔐 Login</h2>
              <span>Enter your email and password</span>

              <form onSubmit={handleSubmit}>
                <label style={{ width: "100%" }} htmlFor="login-emailOrPhone">
                  <span className="sr-only">Email Address</span>
                  <input
                    id="login-emailOrPhone"
                    className="input-control"
                    type="email"
                    name="emailOrPhone"
                    placeholder="Email Address"
                    value={formData.emailOrPhone}
                    onChange={handleChange}
                    required
                  />
                </label>
                <div className="password-input-wrapper">
                  <label style={{ width: "100%", display: "block" }} htmlFor="login-password">
                    <span className="sr-only">Password</span>
                    <input
                      id="login-password"
                      className="input-control"
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                    />
                  </label>
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                        <line x1="1" y1="1" x2="23" y2="23"></line>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    )}
                  </button>
                </div>
                <button className="btn btn-primary-gradient login-btn" type="submit" disabled={loading}>
                  {loading ? "Logging in..." : "Login"}
                </button>
              </form>

              {error && <p className="error-message">{error}</p>}
              {success && <p className="success-message">{success}</p>}

              {/* 🚨 UNVERIFIED USER GUIDANCE & DIRECT VERIFY BUTTON */}
              {unverifiedUser && (
                <div className="login-unverified-prompt">
                  <div className="login-unverified-header">
                    <span>⚠️</span>
                    <strong>Account Not Verified</strong>
                  </div>
                  <div className="login-unverified-body">
                    Your email has not been verified yet. If you haven't received the OTP code, please check your <strong>SPAM</strong>, <strong>JUNK</strong>, or <strong>PROMOTIONS</strong> folder!
                  </div>
                  <button
                    type="button"
                    className="btn-login-verify-now"
                    onClick={() => {
                      localStorage.setItem("pendingVerifyEmail", unverifiedUser);
                      navigate("/verify-email-otp", { state: { emailOrPhone: unverifiedUser } });
                    }}
                  >
                    ✉️ Enter OTP & Verify Now ➔
                  </button>
                </div>
              )}

              {/* Forgot Password Link */}
              <p className="footer-link">
                Forgot password? <Link to="/forgot-password">Reset here</Link>
              </p>

              <div className="auth-switch-card">
                <div className="auth-switch-info">
                  <span>New user?</span>
                </div>
                <Link
                  to={uCode ? `/register?uCode=${uCode}` : "/register"}
                  className="btn-auth-switch"
                  id="login-register-btn"
                >
                  <span>Register</span>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M5 12h14"></path>
                    <path d="m12 5 7 7-7 7"></path>
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;